/**
 * Phase 46 (Plan 46-07) — Notification taxonomy frontend constants.
 *
 * Mirrors the backend Zod enums from `backend/src/services/projectNotificationRoutesService.ts`
 * (`NOTIFICATION_CHANNELS` + `EVENT_KINDS`). All labels are English per
 * `feedback_english_only_ui.md` memory + the Phase 41-14 no-czech-in-v7 grep
 * gate. Per UI-SPEC §Channel & event labels, the "balíček" domain term is
 * translated to "monthly hour package" (planner Option A; zero Czech
 * literals in this file).
 *
 * Phase 103 (D-08): EVENT_KINDS widened from 7 to all 16 members, in the
 * same family order as `backend/src/services/notificationKinds.ts`. This is
 * a different package from the backend, so it cannot import the canonical
 * list directly — `backend/tests/notificationEventKindDrift.test.ts` reads
 * this file from disk and asserts set-equality against `NOTIFICATION_KINDS`
 * instead. `EVENT_KIND_LABELS` and `EVENT_KIND_TOOLTIPS` are
 * `Record<EventKind, string>`, so a missing entry for one of the 9 new
 * kinds is a compile error, not a silent gap.
 */

export const EVENT_KINDS = [
  'sla.warning',
  'sla.breach',
  'package.threshold_crossed',
  'package.cap_blocked',
  'package.zero_rate_overage',
  'package.overage_threshold_crossed',
  'package.overage_approved',
  'training.due',
  'training.overdue',
  'task.assigned',
  'task.activity',
  'bot_usage.threshold_crossed',
  'kudos_received',
  'task_intake_submitted',
  'task_intake_tier_assigned',
  'task_intake_nag',
  // Quick task 260914-pxp — notify a project's PM when a human creates an
  // internal task in their project.
  'task.created',
  // MKS-209 (billing revision D) — the monthly "you logged nothing on this
  // project" reminder: a DM to the person, a grouped summary to the PMs.
  'nothing_logged',
  // MKS-265 — backdated-hours requests: the project's PM is told one arrived,
  // and the requester is told it was approved or declined.
  'backdated_hours.requested',
  'backdated_hours.decided',
  // MKS-139 — the daily billing-schedule sweep: a period about to end, a
  // phase past its planned date, a period stuck in "ready to invoice", and an
  // unpaid invoice past its due date.
  'billing_period.ending_soon',
  'billing_period.phase_overdue',
  'billing_period.ready_not_invoiced',
  'invoice.overdue',
  // MKS-341 — the daily ticket-due-date sweep: the assignee is told the day
  // before a ticket's due date and again on it.
  'task.due_tomorrow',
  'task.due_today',
  // MKS-361 — the stand-in is told, when a colleague's time off is approved,
  // which of their projects they are covering.
  'time_off.substitute_assigned',
  // MKS-410 — the person who filed a helpdesk request is told when an admin
  // resolves or rejects it, with the resolution note or the rejection reason.
  'helpdesk_request.resolved',
  'helpdesk_request.rejected',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export const CHANNELS = [
  'email',
  'in_app',
  'slack_dm',
  'slack_channel',
  'slack_thread',
  'sms',
] as const;
export type Channel = (typeof CHANNELS)[number];

export type DispatchStatus = 'sent' | 'failed' | 'skipped';

export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  'sla.warning': 'SLA warning threshold crossed',
  'sla.breach': 'SLA breach',
  'package.threshold_crossed': 'Package alert threshold crossed',
  'package.cap_blocked': 'Package cap reached — blocked',
  'package.zero_rate_overage': 'Overage with zero billing rate',
  'package.overage_threshold_crossed': 'Pre-approved overage crossed',
  'package.overage_approved': 'Overage approved',
  'training.due': 'Training due soon',
  'training.overdue': 'Training overdue',
  'task.assigned': 'Task assigned',
  'task.activity': 'Task activity',
  'bot_usage.threshold_crossed': 'AI assistant usage threshold crossed',
  kudos_received: 'Kudos received',
  task_intake_submitted: 'Task intake submitted',
  task_intake_tier_assigned: 'Task intake tier assigned',
  task_intake_nag: 'Task intake nag reminder',
  'task.created': 'Task created',
  nothing_logged: 'Nothing logged this month',
  'backdated_hours.requested': 'Backdated hours requested',
  'backdated_hours.decided': 'Backdated hours approved or declined',
  'billing_period.ending_soon': 'Billing period ending soon',
  'billing_period.phase_overdue': 'Billing phase overdue',
  'billing_period.ready_not_invoiced': 'Ready to invoice but not invoiced',
  'invoice.overdue': 'Invoice overdue',
  'task.due_tomorrow': 'Ticket due tomorrow',
  'task.due_today': 'Ticket due today',
  'time_off.substitute_assigned': 'You are covering a colleague',
  'helpdesk_request.resolved': 'Helpdesk request resolved',
  'helpdesk_request.rejected': 'Helpdesk request rejected',
};

export const EVENT_KIND_TOOLTIPS: Record<EventKind, string> = {
  'sla.warning':
    'Task response or resolution timer crossed a warning threshold for its tier',
  'sla.breach': 'Task response or resolution timer exceeded the tier target',
  'package.threshold_crossed':
    'Project monthly hour package consumption crossed an alert threshold (50% / 80% / 100%)',
  'package.cap_blocked':
    'Time entry blocked because monthly hour package is exhausted and overage is not allowed',
  'package.zero_rate_overage':
    'Time entry saved as overage but the role has no overage rate configured',
  'package.overage_threshold_crossed':
    "Overage consumption crossed the PM's approved overage value",
  'package.overage_approved':
    'PM recorded a new approved overage value for the current month',
  'training.due':
    'A required training course is approaching its due date. Goes to the affected employee only.',
  'training.overdue':
    'A required training course is past its due date. Goes to the affected employee only.',
  'task.assigned':
    'A task was assigned to someone. Goes to the new assignee. External assignees receive email only — the client portal has no in-app notification surface.',
  'task.activity':
    "A watched task changed (comment, state, description, acceptance, or assignee). Goes to the task's watchers. External watchers receive email only.",
  'bot_usage.threshold_crossed':
    'An AI-assistant usage threshold was crossed. Goes to every admin. This kind has no email implementation — routing it to the email channel produces a skipped audit row, not a message.',
  kudos_received: 'A colleague recognised someone with kudos. Goes to the recipient.',
  task_intake_submitted:
    'A client submitted a task through the intake form. Goes to admins, the PM, and (on SLA projects) the coverage team.',
  task_intake_tier_assigned:
    'An incoming task was triaged as P1/P2. Goes to the on-call coverage team. This is the one event kind with a Slack DM channel.',
  task_intake_nag:
    'A submitted task has sat untriaged for over 24 hours. Goes to the PM.',
  'task.created':
    "A human created an internal task in a project they don't manage. Goes to the project's PM, across in-app, email, and Slack DM. The creator is skipped entirely when they are the PM themselves.",
  nothing_logged:
    'Near month end (and again early the next month), someone assigned to a project logged no time on it for the month. The Slack DM goes to that person; the Slack channel route posts one summary grouped by project for the PMs — set its target to the PM channel. People on leave for the whole period and assignments that ended before the period are never included.',
  'backdated_hours.requested':
    "Someone asked for hours to be added to a project-month that is already locked. Goes to that project's PM. The requester is never notified of their own request.",
  'backdated_hours.decided':
    'A backdated-hours request was approved or declined. Goes to the person who filed it, and carries the decline reason when there is one. In-app is the fallback lane: the row is written only when the Slack DM did not go out, so nobody is told twice.',
  'billing_period.ending_soon':
    "A billing period's planned end date is a configurable number of days away (default 7). Goes to the project's PM, once per period.",
  'billing_period.phase_overdue':
    "A billing phase passed its planned end date and nobody has marked it done. Goes to the project's PM and repeats at most once a week until the phase is closed.",
  'billing_period.ready_not_invoiced':
    'A billing period has been ready to invoice for longer than the configured number of days (default 5) and still has no invoice. Goes to finance, once per period.',
  'invoice.overdue':
    "An invoice is past its due date and is not marked paid. Goes to finance and to the PMs of the invoice's projects, repeating at most once a week until it is marked paid. Invoices with no due date are never overdue.",
  'task.due_tomorrow':
    "A ticket with a due date is due tomorrow. Goes to the ticket's assignee, at most once per due date. Tickets in a terminal state and tickets with no due date are never notified.",
  'task.due_today':
    "A ticket with a due date is due today. Goes to the ticket's assignee, at most once per due date, and is a separate notice from the day-before nudge so either can be muted on its own.",
  'time_off.substitute_assigned':
    'A colleague named you as their substitute on one or more projects and their time off has been approved. Goes to the named person once, listing every project they cover and any note the requester left. Nothing is sent while the request is still waiting for approval.',
  'helpdesk_request.resolved':
    'An admin resolved a helpdesk request, with the resolution note the status flow requires. Goes to the person who filed it, as a Slack DM, falling back to an in-app notification when they have no Slack handle. Never by email. A client who filed through the portal is not notified this way — they hear back on their own ticket.',
  'helpdesk_request.rejected':
    'An admin rejected a helpdesk request, with the reason the status flow requires. Goes to the person who filed it on the same Slack-DM-then-in-app lane as a resolution, and is a separate notice so either can be muted on its own. Never by email.',
};

export const CHANNEL_LABELS: Record<
  Channel,
  { label: string; target: 'none' | 'required' | 'optional' }
> = {
  email: { label: 'Email', target: 'none' },
  in_app: { label: 'In-app', target: 'none' },
  slack_dm: { label: 'Slack DM', target: 'none' },
  slack_channel: { label: 'Slack channel', target: 'required' },
  slack_thread: { label: 'Slack thread', target: 'required' },
  sms: { label: 'SMS', target: 'none' },
};

export const CHANNEL_TOOLTIPS: Record<Channel, string> = {
  email: "Delivered to the recipient's email address",
  in_app: 'Notification bell + /notifications page',
  slack_dm: 'Direct message — requires Slack handle on user profile',
  slack_channel: 'Slack channel name (#channel) or channel ID (e.g. C0123ABC)',
  slack_thread: 'Thread permalink URL or channel_id:ts token',
  sms: 'SMS — requires phone number on user profile',
};

export const STATUS_LABELS: Record<DispatchStatus, string> = {
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
};

/**
 * Maps backend skip reason codes to human-readable English labels. Backend
 * emits reason codes such as `missing_target` from the Wave 3 dispatcher
 * fan-out per `backend/src/services/notificationAuditService.ts`.
 */
export const SKIP_REASON_LABELS: Record<string, string> = {
  missing_target: 'Missing channel target',
  orphan_pm: 'Project has no PM',
  recipient_deleted: 'Recipient user deleted',
  soft_deleted_pm: 'PM was soft-deleted',
  no_recipient_resolved: 'No recipient could be resolved',
  channel_disabled: 'Channel disabled for recipient',
  // Phase 103 (D-10): written by splitUnsupportedChannels when an admin
  // routes an event kind to a channel its producer has no code path for
  // (e.g. bot_usage.threshold_crossed routed to email).
  channel_unsupported: 'Channel not implemented for this event',
};

/**
 * Convenience helper — looks up the human label for a skip reason or falls
 * back to the raw code when unknown.
 */
export function formatSkipReason(reason: string | null | undefined): string {
  if (!reason) return '—';
  return SKIP_REASON_LABELS[reason] ?? reason;
}
